import React, { useCallback, useEffect, useState } from "react";
import { BookOpen, Loader2, Plus, RefreshCw, Save } from "lucide-react";
import { hasPermission } from "../../../utils/permissionUtils";
import {
  getEmployeeDocumentAccessStatus,
  grantDocumentAccessBonus,
  patchEmployeeDocumentAccessPolicy,
} from "../../../api/elearning/documents/documentAccess";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";

function canManageDocumentAccess() {
  return (
    hasPermission("hrm.employees", "U") ||
    hasPermission("elearning.documents", "U") ||
    hasPermission("crm.teacher_accounts", "U")
  );
}

/**
 * Quản lý hạn mức tải/xem tài liệu e-learning (theo tháng).
 * Chỉ hiển thị khi có quyền cập nhật nhân sự hoặc quản lý tài liệu e-learning.
 */
const EmployeeDocumentAccessPanel = ({ employeeId }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [monthlyLimitInput, setMonthlyLimitInput] = useState("5");
  const [exemptInput, setExemptInput] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [granting, setGranting] = useState(false);

  const load = useCallback(async () => {
    if (!employeeId || !canManageDocumentAccess()) return;
    setLoading(true);
    try {
      const s = await getEmployeeDocumentAccessStatus(employeeId);
      setStatus(s);
      setMonthlyLimitInput(String(s.monthlyLimit ?? 5));
      setExemptInput(!!s.exempt);
    } catch (e) {
      setStatus(null);
      showErrorToast(
        e?.response?.data?.message ||
          e?.message ||
          "Không tải được hạn mức tài liệu e-learning."
      );
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (!employeeId || !canManageDocumentAccess()) {
      setStatus(null);
      return;
    }
    load();
  }, [employeeId, load]);

  if (!employeeId || !canManageDocumentAccess()) return null;

  const handleSavePolicy = async () => {
    const n = Math.min(1000, Math.max(0, parseInt(monthlyLimitInput, 10) || 0));
    setSavingPolicy(true);
    try {
      const res = await patchEmployeeDocumentAccessPolicy(employeeId, {
        monthly_limit: n,
        doc_quota_exempt: exemptInput,
      });
      if (res?.status) {
        setStatus(res.status);
        setMonthlyLimitInput(String(res.status.monthlyLimit ?? n));
        setExemptInput(!!res.status.exempt);
      } else {
        await load();
      }
      showSuccessToast(res?.message || "Đã cập nhật hạn mức.");
    } catch (e) {
      showErrorToast(
        e?.response?.data?.message || e?.message || "Không lưu được cài đặt."
      );
    } finally {
      setSavingPolicy(false);
    }
  };

  const handleGrant = async () => {
    setGranting(true);
    try {
      const res = await grantDocumentAccessBonus(employeeId, { packs: 1 });
      if (res?.status) {
        setStatus(res.status);
        setMonthlyLimitInput(String(res.status.monthlyLimit ?? monthlyLimitInput));
        setExemptInput(!!res.status.exempt);
      } else {
        await load();
      }
      showSuccessToast(res?.message || "Đã cấp thêm 5 lượt.");
    } catch (e) {
      showErrorToast(
        e?.response?.data?.message || e?.message || "Không cấp được lượt."
      );
    } finally {
      setGranting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm space-y-4">
      <div className="flex items-center justify-between gap-3 pb-2 border-b border-gray-50 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 bg-red-50 rounded-lg shrink-0">
            <BookOpen className="w-5 h-5 text-red-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-tight">
              Lượt tải / xem tài liệu E-Learning
            </h3>
            <p className="text-xs text-gray-400 leading-snug">
              Mỗi lần tải hoặc xem qua API tài liệu học tập tính 1 lượt trong tháng (mặc định 5 + lượt
              bonus).
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold uppercase text-gray-600 hover:bg-gray-50 disabled:opacity-50 shrink-0"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Làm mới
        </button>
      </div>

      {loading && !status ? (
        <div className="flex items-center justify-center gap-2 py-8 text-gray-500">
          <Loader2 className="animate-spin" size={22} />
          <span className="text-sm font-medium">Đang tải…</span>
        </div>
      ) : status ? (
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="rounded-xl bg-slate-50 p-3 border border-gray-100">
              <p className="text-[10px] font-bold uppercase text-gray-400">Tháng</p>
              <p className="mt-1 font-bold text-gray-900">{status.yearMonth}</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3 border border-gray-100">
              <p className="text-[10px] font-bold uppercase text-gray-400">Đã dùng</p>
              <p className="mt-1 font-bold text-gray-900">
                {status.usedThisMonth} / {status.allowance}
              </p>
            </div>
            <div className="rounded-xl bg-emerald-50 p-3 border border-emerald-100">
              <p className="text-[10px] font-bold uppercase text-emerald-700">Còn lại</p>
              <p className="mt-1 font-bold text-emerald-900">{status.remaining}</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3 border border-gray-100">
              <p className="text-[10px] font-bold uppercase text-gray-400">Lượt bonus</p>
              <p className="mt-1 font-bold text-gray-900">{status.bonusBalance}</p>
            </div>
          </div>

          {status.exempt ? (
            <p className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
              Tài khoản đang <span className="font-black">miễn trừ</span> hạn mức (admin / kỹ thuật).
            </p>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400 block mb-1.5">
                Hạn mức mỗi tháng (0–1000)
              </label>
              <input
                type="number"
                min={0}
                max={1000}
                value={monthlyLimitInput}
                onChange={(e) => setMonthlyLimitInput(e.target.value)}
                className="w-full border rounded-xl px-4 py-2.5 border-gray-200 text-sm font-semibold text-gray-800 outline-none focus:ring-2 focus:ring-red-100 focus:border-red-400"
              />
            </div>
            <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/50 cursor-pointer md:mt-6">
              <input
                type="checkbox"
                checked={exemptInput}
                onChange={(e) => setExemptInput(e.target.checked)}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500 w-4 h-4"
              />
              <span className="text-sm font-bold text-gray-700 uppercase tracking-tight">
                Miễn trừ hạn mức tài liệu
              </span>
            </label>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSavePolicy}
              disabled={savingPolicy}
              className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-xs font-bold uppercase text-white hover:bg-red-700 disabled:opacity-60"
            >
              {savingPolicy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Lưu cài đặt
            </button>
            <button
              type="button"
              onClick={handleGrant}
              disabled={granting}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-bold uppercase text-gray-800 hover:bg-gray-50 disabled:opacity-60"
            >
              {granting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Cấp thêm lượt bonus (+5)
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500 py-4">Chưa có dữ liệu hạn mức.</p>
      )}
    </div>
  );
};

export default EmployeeDocumentAccessPanel;
